/* Arlo & Co — Arch Wall Magnet builder.
 *
 * Draws the customer's magnet on the admin's room mockup, then adds it to the
 * cart with a preview PNG (uploaded to Uploadcare) plus readable text
 * properties for production. No text is rendered on this product.
 *
 * Geometry note: all artwork lives in the ARCH FRAME — millimetres, y-down,
 * with the arch occupying 0,0 → 750,950. Borders reach ~100mm past it and use
 * negative coordinates. That's the same frame the admin preview uses, so what
 * Damian sets on the Magnets page is what ships.
 */
(function () {
  'use strict';

  const root = document.getElementById('arlo-mb');
  if (!root) return;

  const cfg = window.ArloMagnetConfig;
  if (!cfg || !cfg.art) {
    console.warn('[ArloMagnets] no config — is assets/arlo-magnet-config.js deployed?');
    return;
  }

  let data;
  try {
    data = JSON.parse(document.getElementById('arlo-mb-data').textContent);
  } catch (e) {
    console.error('[ArloMagnets] bad product data', e);
    return;
  }

  const ART = cfg.art;
  const el = (id) => document.getElementById(id);

  // Sale mode (2026-10-07): this product is in a collection flagged "on sale"
  // in the admin. During a sale the Buy X Get Y add-on discounts are switched
  // off in Shopify, so add-ons are charged at full price — show them at full
  // price with no SAVE tag, or the page under-quotes what checkout charges.
  // Same rule as snippets/canvas-capture.liquid: in a sale collection, or the
  // whole store is on sale and this product isn't in an excluded collection.
  const inCols = (list) => (list || []).some((h) => (data.collections || []).indexOf(h) !== -1);
  const saleMode = inCols(cfg.saleSuppressCollections) || (!!cfg.saleModeAll && !inCols(cfg.saleExcludeCollections));
  // "Was" price for an add-on. In sale mode the Buy X Get Y saving is off, so
  // only the add-on's own Shopify markdown counts (compareAtPrice, refreshed on
  // deploy / Apply sale mode); otherwise the normal full price.
  // Prices for an add-on: full = its Shopify price, cmp = its Shopify
  // compare-at, bxgy = the price after the Buy X Get Y saving. The border uses
  // the DESIGN the customer picked (2026-10-11): each design is its own Shopify
  // variant and can be marked down on its own (e.g. only Pastel Rainbow).
  function addonPricing(a) {
    let full = Number(a.fullPrice || 0);
    let cmp = Number(a.compareAtPrice || 0);
    let bxgy = a.noDiscount ? full : Number(a.discountedPrice || full);
    if (a.kind === 'border') {
      const b = borders()[state.borderIdx]; // same list/index the design tiles use
      if (b && Number(b.addonPrice) > 0) {
        full = Number(b.addonPrice);
        cmp = Number(b.addonCompareAt || 0);
        bxgy = a.noDiscount ? full : Math.max(0, full - Number(a.discountAmount || 0));
      }
    }
    return { full, cmp, bxgy };
  }
  function addonWasPrice(a) {
    const p = addonPricing(a);
    return saleMode && p.cmp > p.full ? p.cmp : p.full;
  }
  function addonNowPrice(a) {
    const p = addonPricing(a);
    return saleMode ? p.full : p.bxgy;
  }
  // Price + SAVE tag markup for an add-on card (redrawn when the border design changes).
  function addonPriceParts(a) {
    const full = addonWasPrice(a), disc = addonNowPrice(a);
    const saving = full > disc && disc > 0 ? 'SAVE ' + money(full - disc) : '';
    const was = full > disc && disc > 0 ? `<span class="arlo-mb__upsell-was">${money(full)}</span>` : '';
    const badgeStyle = saleMode && cfg.saleColour ? `background:${cfg.saleColour};color:${cfg.saleTextColour || '#2b2b2b'}` : '';
    return { saving, priceHtml: `${was}${money(disc || full)}`, badgeStyle };
  }

  const state = {
    colourIdx: 0,
    borderIdx: 0,           // only applies once the border add-on is ticked
    packIdx: 0,             // legacy; the colour is the variant now
    addons: {},          // id → true
    slide: 'preview',
  };
  let _atcBusy = false;

  // ── assets ────────────────────────────────────────────────────────────────
  const imgCache = {};
  function loadImage(url) {
    if (!url) return Promise.resolve(null);
    if (imgCache[url]) return imgCache[url];
    imgCache[url] = new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = () => resolve(null);
      img.src = url;
    });
    return imgCache[url];
  }

  // Shopify rewrites asset filenames, so the road PNG and room mockup URLs are
  // handed in by the section rather than guessed.
  const ROAD_SRC = root.dataset.roadSrc || '';
  // Asset folder, derived from the one URL Liquid gives us. The road's own
  // ?v= hash is stripped: carrying it over meant a replaced room image kept the
  // old cache entry (or a cached 404 from before it existed) and never
  // refreshed. Each layer appends its OWN version instead.
  const ASSET_BASE = ROAD_SRC.split('?')[0].replace(/arlo-magnet-road\.png$/, '');
  function assetSrc(name, version) {
    if (!name || !ASSET_BASE) return '';
    return ASSET_BASE + name + (version ? '?v=' + version : '');
  }

  // Two room layers: the arch is sandwiched between them, so a shelf or plant
  // in the foreground PNG can sit in front of the magnet.
  let roadImg = null, roomImg = null, roomFgImg = null;
  const ready = Promise.all([
    loadImage(ROAD_SRC).then((i) => { roadImg = i; }),
    loadImage(assetSrc((cfg.preview || {}).roomAsset, (cfg.preview || {}).roomAssetV)).then((i) => { roomImg = i; }),
    loadImage(assetSrc((cfg.preview || {}).roomForegroundAsset, (cfg.preview || {}).roomForegroundAssetV)).then((i) => { roomFgImg = i; }),
  ]);

  // ── drawing ───────────────────────────────────────────────────────────────
  const pathCache = {};
  function p2d(d) {
    if (!pathCache[d]) pathCache[d] = new Path2D(d);
    return pathCache[d];
  }

  // Draws a border in the arch frame. Vector designs are bands painted
  // outermost-first; the road is raster art placed by its recorded box.
  function drawBorder(ctx, border) {
    const design = border && ART.designs[border.design];
    if (!design) return;
    if (design.type === 'raster') {
      if (roadImg) {
        const b = design.boxMm;
        ctx.drawImage(roadImg, b.x, b.y, b.w, b.h);
      }
      return;
    }
    design.bands.forEach((band) => {
      ctx.fillStyle = band.hex || (border.colour || {}).hex || '#999999';
      ctx.fill(p2d(band.d), band.evenOdd ? 'evenodd' : 'nonzero');
    });
  }

  // Composite onto a scene using the admin's placement percentages.
  function drawScene(ctx, W, H, opts) {
    const o = opts || {};
    if (roomImg && !o.noRoom) ctx.drawImage(roomImg, 0, 0, W, H);
    else { ctx.fillStyle = (cfg.preview && cfg.preview.wallHex) || '#F2EFEA'; ctx.fillRect(0, 0, W, H); }

    const pa = (cfg.preview && cfg.preview.arch) || { centreXPct: 50, bottomYPct: 82, heightPct: 55 };
    const archH = H * (pa.heightPct / 100);
    const sc = archH / ART.archMm.h;
    const archW = ART.archMm.w * sc;
    const x = W * (pa.centreXPct / 100) - archW / 2;
    const y = H * (pa.bottomYPct / 100) - archH;

    ctx.save();
    ctx.translate(x, y);
    ctx.scale(sc, sc);
    const border = currentBorder();
    if (border) drawBorder(ctx, border);
    const arch = p2d(ART.archPath);
    ctx.fillStyle = (currentColour() || {}).hex || '#ccc';
    ctx.fill(arch);
    ctx.strokeStyle = 'rgba(0,0,0,.10)';
    ctx.lineWidth = 1 / sc;
    ctx.stroke(arch);
    ctx.restore();

    // Foreground layer goes on last so the magnet reads as being behind it
    if (roomFgImg && !o.noRoom) ctx.drawImage(roomFgImg, 0, 0, W, H);
  }

  // Small standalone thumbnail of one border choice (no room behind it). The
  // arch inside is filled with the colour the customer has actually chosen, so
  // each button previews the real combination rather than a generic white arch.
  function drawBorderThumb(cv, border, archHex) {
    const ctx = cv.getContext('2d');
    ctx.clearRect(0, 0, cv.width, cv.height);
    // border === null draws the arch on its own, for the "No border" tile
    const reach = border ? ((ART.designs[border.design] || {}).reachMm || 100) : 40;
    const w = ART.archMm.w + reach * 2, h = ART.archMm.h + reach;
    const sc = Math.min(cv.width / w, cv.height / h);
    ctx.save();
    ctx.translate((cv.width - w * sc) / 2, (cv.height - h * sc) / 2);
    ctx.scale(sc, sc);
    ctx.translate(reach, reach);
    if (border) drawBorder(ctx, border);
    const arch = p2d(ART.archPath);
    ctx.fillStyle = archHex || (currentColour() || {}).hex || '#ffffff';
    ctx.fill(arch);
    // hairline keeps a pale arch readable against a pale border
    ctx.strokeStyle = 'rgba(0,0,0,.12)';
    ctx.lineWidth = 2 / sc;
    ctx.stroke(arch);
    ctx.restore();
  }

  // Repaint every border button in the current arch colour, without rebuilding
  // the DOM (rebuilding blanks the canvases and makes them flash).
  function repaintBorderThumbs() {
    borders().forEach((b, i) => {
      const cv = document.querySelector(`[data-mb-border-cv="${i}"]`);
      if (cv) drawBorderThumb(cv, b);
    });
    drawAddonThumbs();
  }

  // ── state helpers ─────────────────────────────────────────────────────────
  const colours = () => cfg.archColours || [];
  const borders = () => (cfg.borders || []).filter((b) => b.enabled !== false);
  const currentColour = () => colours()[state.colourIdx];
  // A border is only part of the order once the add-on card is ticked. Until
  // then step 2 is hidden and the arch is previewed bare.
  const borderAddonTicked = () => (cfg.addons || []).some((a) => a.kind === 'border' && state.addons[a.id]);
  const borderApplies = () => borderAddonTicked();
  const currentBorder = () => (borderApplies() && state.borderIdx >= 0 ? borders()[state.borderIdx] : null);

  // The arch colour IS the variant: stock is per colour and a sold-out colour
  // can't be bought. Prefer the ID recorded in the admin, fall back to position.
  function colourVariant() {
    const c = currentColour();
    if (!c) return null;
    if (c.variantId) {
      const hit = data.variants.find((v) => String(v.id) === String(c.variantId));
      if (hit) return hit;
    }
    return data.variants[state.colourIdx] || null;
  }
  function borderAddon() {
    return (cfg.addons || []).find((a) => a.kind === 'border' && a.enabled !== false) || null;
  }

  // ── render ────────────────────────────────────────────────────────────────
  // The displayed price is a RUNNING TOTAL: the arch variant plus every ticked
  // add-on at its discounted price. Without this the customer sees $49.95 but
  // lands on $69.90 at checkout, because the border is ticked by default.
  function priceTotals() {
    const v = colourVariant();
    let now = v ? Number(v.price) : 0;                 // cents
    let was = v ? Number(v.compare_at_price || v.price) : 0;
    for (const a of visibleAddons()) {
      if (!state.addons[a.id]) continue;
      const full = Math.round(addonWasPrice(a) * 100);
      const disc = Math.round(addonNowPrice(a) * 100);
      now += disc;
      was += full;
    }
    return { now, was };
  }

  function renderPrice() {
    const box = el('arlo-mb-price');
    if (!box || !colourVariant()) return;
    const { now, was } = priceTotals();
    const priceEl = box.querySelector('[data-price]');
    const compareEl = box.querySelector('[data-compare-price]');
    if (priceEl) priceEl.textContent = formatMoney(now);
    if (compareEl) compareEl.textContent = was > now ? formatMoney(was) : '';
  }

  // Currency format is taken once from what Liquid printed, so it matches the
  // rest of the site and can't drift as we overwrite the element.
  const MONEY_FMT = (function () {
    const box = document.getElementById('arlo-mb-price');
    const sample = (box && box.querySelector('[data-price]') && box.querySelector('[data-price]').textContent) || '$0.00';
    return {
      symbol: (sample.match(/^[^\d]*/) || ['$'])[0] || '$',
      withCents: /[.,]\d\d/.test(sample),
    };
  }());

  function formatMoney(cents) {
    const n = Number(cents || 0) / 100;
    return MONEY_FMT.symbol + (MONEY_FMT.withCents ? n.toFixed(2) : Math.round(n).toLocaleString());
  }

  function renderSwatches() {
    el('arlo-mb-swatches').innerHTML = colours().map((c, i) => `
      <button type="button" class="arlo-mb__swatch${i === state.colourIdx ? ' active' : ''}"
        data-mb-colour="${i}" title="${c.name}" aria-label="${c.name}"
        style="background:${c.hex}"></button>`).join('');
    el('arlo-mb-colour-name').textContent = (currentColour() || {}).name || '';
  }

  function renderBorders() {
    const box = el('arlo-mb-borders'); // lives inside the border add-on's expand panel
    if (box) {
      const list = borders();
      box.innerHTML = list.map((b, i) => `
        <div class="arlo-mb__border${i === state.borderIdx ? ' active' : ''}" data-mb-border="${i}">
          <canvas data-mb-border-cv="${i}" width="150" height="150"></canvas>
          <div class="arlo-mb__border-label">${b.label || b.design}</div>
        </div>`).join('');
      list.forEach((b, i) => {
        const cv = document.querySelector(`[data-mb-border-cv="${i}"]`);
        if (cv) drawBorderThumb(cv, b);
      });
    }
    const note = el('arlo-mb-border-note');
    if (note) note.textContent = 'Pick a design — it’s added as its own item on your order.';
    updateStepVisibility();
  }

  // Add-ons is step 2. Keep its badge fixed and show/hide the border design
  // panel to match whether the border add-on is ticked.
  function updateStepVisibility() {
    const badge = el('arlo-mb-addons-badge');
    if (badge) badge.textContent = '2';
    syncBorderPanel();
  }

  // Expand the border design tiles when the decal-border add-on is ticked.
  function syncBorderPanel() {
    const a = borderAddon();
    if (!a) return;
    const panel = document.querySelector(`[data-mb-border-panel="${a.id}"]`);
    if (panel) panel.style.display = state.addons[a.id] ? 'block' : 'none';
  }

  function money(n) {
    const v = Number(n || 0);
    return '$' + (Math.abs(v - Math.round(v)) < 0.005 ? String(Math.round(v)) : v.toFixed(2));
  }

  function variantPrice(i) {
    const v = data.variants[i];
    return v ? Number(v.price) / 100 : null;
  }


  function visibleAddons() {
    return (cfg.addons || []).filter((a) => a.enabled !== false);
  }

  function renderAddons() {
    const box = el('arlo-mb-upsell');
    const stepBox = el('arlo-mb-step-addons');
    const list = visibleAddons();
    if (!list.length) {
      box.style.display = 'none'; box.innerHTML = ''; box.dataset.built = '';
      if (stepBox) stepBox.style.display = 'none';
      return;
    }
    box.style.display = 'block';
    if (stepBox) stepBox.style.display = '';

    const signature = list.map((a) => a.id).join('|');
    // Rebuild only when the set of cards changes — re-rendering on every colour
    // change would blank the thumbnail canvases and make them flash.
    if (box.dataset.built === signature) {
      list.forEach((a) => {
        const card = box.querySelector(`[data-mb-addon="${a.id}"]`);
        if (!card) return;
        card.classList.toggle('active', !!state.addons[a.id]);
        // Prices can change with the chosen border design — update in place
        // (rebuilding would blank the thumbnail canvases).
        const pp = addonPriceParts(a);
        const priceEl = card.querySelector('.arlo-mb__upsell-price');
        if (priceEl) priceEl.innerHTML = pp.priceHtml;
        let badge = card.querySelector('.arlo-mb__upsell-badge');
        if (pp.saving) {
          if (!badge) { badge = document.createElement('span'); badge.className = 'arlo-mb__upsell-badge'; card.insertBefore(badge, card.firstChild); }
          badge.textContent = pp.saving;
          badge.setAttribute('style', pp.badgeStyle);
        } else if (badge) {
          badge.remove();
        }
      });
      syncBorderPanel();
      return;
    }

    box.innerHTML = list.map((a) => {
      const pp = addonPriceParts(a);
      const card = `
      <div class="arlo-mb__upsell-card${state.addons[a.id] ? ' active' : ''}" data-mb-addon="${a.id}">
        ${pp.saving ? `<span class="arlo-mb__upsell-badge" style="${pp.badgeStyle}">${pp.saving}</span>` : ''}
        <span class="arlo-mb__upsell-check">✓</span>
        <span class="arlo-mb__upsell-thumb"><canvas data-mb-addon-cv="${a.id}" width="120" height="120"></canvas></span>
        <span class="arlo-mb__upsell-text">
          <span class="arlo-mb__upsell-title">${a.title || 'Add-on'}</span>
          <span class="arlo-mb__upsell-desc">${a.description || ''}</span>
        </span>
        <span class="arlo-mb__upsell-price">${pp.priceHtml}</span>
      </div>`;
      // The border add-on expands to show the design tiles when ticked. The
      // panel sits ABOVE the card so the tiles are nearer the top (and the main
      // image preview stays in view while switching designs).
      if (a.kind === 'border') {
        const panel = `
      <div class="arlo-mb__border-panel" data-mb-border-panel="${a.id}" style="display:${state.addons[a.id] ? 'block' : 'none'}">
        <div class="arlo-mb__border-note" id="arlo-mb-border-note"></div>
        <div class="arlo-mb__borders" id="arlo-mb-borders"></div>
      </div>`;
        return panel + card;
      }
      return card;
    }).join('');
    box.dataset.built = signature;
    drawAddonThumbs();
    updateStepVisibility();
  }

  function drawAddonThumbs() {
    visibleAddons().forEach((a) => {
      const cv = document.querySelector(`[data-mb-addon-cv="${a.id}"]`);
      if (!cv) return;
      if (a.kind === 'border') {
        drawBorderThumb(cv, currentBorder() || borders()[0]);
      } else {
        // crayons (or anything else) — a simple set of coloured crayons
        const ctx = cv.getContext('2d');
        ctx.clearRect(0, 0, cv.width, cv.height);
        const cols = ['#D5603A', '#F9B16B', '#FBE767', '#9CDCC4', '#4D8FC3', '#C998D2'];
        const w = cv.width / (cols.length + 2);
        cols.forEach((c, i) => {
          const x = w * (i + 1), top = cv.height * 0.22, bot = cv.height * 0.82;
          ctx.fillStyle = c;
          ctx.fillRect(x, top + 8, w * 0.7, bot - top - 8);
          ctx.beginPath();
          ctx.moveTo(x, top + 8);
          ctx.lineTo(x + w * 0.35, top);
          ctx.lineTo(x + w * 0.7, top + 8);
          ctx.closePath();
          ctx.fill();
        });
      }
    });
  }

  // ── mockup photos ─────────────────────────────────────────────────────────
  // Key must match the admin exactly: "<arch colour name>|<border id>", with
  // 'none' for an arch on its own.
  function mockupKey() {
    const c = currentColour(), b = currentBorder();
    return c ? (c.name + '|' + (b ? b.id : 'none')) : '';
  }

  // Only ever fetches the combination on screen — at 1000px wide from Shopify's
  // CDN, not the full-size original — so the page stays light however many
  // photos exist. `_photoToken` drops responses that land after a newer pick.
  let _photoToken = 0;
  function syncMockupPhoto() {
    const img = el('arlo-mb-photo');
    if (!img) return;
    const url = (cfg.mockups || {})[mockupKey()];
    const token = ++_photoToken;
    if (!url) { img.classList.remove('loaded'); img.style.display = 'none'; img.removeAttribute('src'); return; }

    const sized = url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=1000';
    if (img.dataset.src === sized) { img.style.display = ''; img.classList.add('loaded'); return; }

    // keep the canvas showing until the new photo is actually decoded
    img.classList.remove('loaded');
    const probe = new Image();
    probe.onload = () => {
      if (token !== _photoToken) return;
      img.src = sized;
      img.dataset.src = sized;
      img.style.display = '';
      requestAnimationFrame(() => img.classList.add('loaded'));
    };
    probe.onerror = () => {
      if (token !== _photoToken) return;
      img.classList.remove('loaded');
      img.style.display = 'none';   // fall back to the drawing
    };
    probe.src = sized;
  }

  function renderPreview() {
    const cv = el('arlo-mb-canvas');
    const W = 1000;
    const H = roomImg ? Math.round(W * roomImg.height / roomImg.width) : W;
    if (cv.width !== W || cv.height !== H) { cv.width = W; cv.height = H; }
    drawScene(cv.getContext('2d'), W, H);
    syncMockupPhoto();

  }

  // Default the border add-on OFF — the border is a paid separate item, so the
  // customer opts in rather than having to untick it.
  function primeBorderDefault() {
    const a = borderAddon();
    if (a && state.addons[a.id] === undefined) state.addons[a.id] = false;
  }

  function renderAll() {
    primeBorderDefault();
    renderPrice();
    renderSwatches();
    renderAddons();   // builds the extras cards + the border design panel
    renderBorders();  // fills the design tiles inside that panel
    renderPreview();
    updateAtc();
  }

  function updateAtc() {
    const btn = el('arlo-mb-atc');
    const v = colourVariant();
    const sold = v && v.available === false;
    btn.disabled = !v || sold;
    btn.textContent = !v ? 'Choose your options' : (sold ? 'Sold out' : 'Add to cart');
  }

  // ── gallery ───────────────────────────────────────────────────────────────
  function showSlide(key) {
    state.slide = key;
    root.querySelectorAll('[data-mb-slide]').forEach((s) => {
      s.classList.toggle('active', s.dataset.mbSlide === String(key));
    });
    root.querySelectorAll('[data-mb-dot]').forEach((d) => {
      d.classList.toggle('active', d.dataset.mbDot === String(key));
    });
  }

  // Changing an option always brings the builder image back — otherwise the
  // customer edits their magnet while looking at a lifestyle photo and sees
  // nothing happen.
  function snapToPreview() {
    if (state.slide !== 'preview') showSlide('preview');
  }

  function slideKeys() {
    return Array.from(root.querySelectorAll('[data-mb-slide]')).map((s) => s.dataset.mbSlide);
  }

  function stepSlide(dir) {
    const keys = slideKeys();
    const i = keys.indexOf(String(state.slide));
    const j = i + dir;
    if (j < 0 || j >= keys.length) return;
    showSlide(keys[j]);
  }

  // ── events ────────────────────────────────────────────────────────────────
  root.addEventListener('click', (e) => {
    const sw = e.target.closest('[data-mb-colour]');
    if (sw) {
      state.colourIdx = parseInt(sw.dataset.mbColour, 10);
      snapToPreview();
      renderSwatches(); renderPrice(); renderPreview(); repaintBorderThumbs(); updateAtc();
      scheduleThumbWarm();
      return;
    }
    const bd = e.target.closest('[data-mb-border]');
    if (bd) {
      state.borderIdx = parseInt(bd.dataset.mbBorder, 10);
      snapToPreview();
      renderBorders(); renderPreview(); drawAddonThumbs(); updateAtc();
      renderAddons(); renderPrice(); // each design has its own price (may be on sale)
      scheduleThumbWarm();
      return;
    }
    const ad = e.target.closest('[data-mb-addon]');
    if (ad) {
      const id = ad.dataset.mbAddon;
      state.addons[id] = !state.addons[id];
      ad.classList.toggle('active', !!state.addons[id]);
      snapToPreview();
      renderBorders();     // shows or hides step 2 for the border add-on
      renderPreview();
      renderPrice();       // running total follows the ticks
      scheduleThumbWarm();
      return;
    }
    const dot = e.target.closest('[data-mb-dot]');
    if (dot) { showSlide(dot.dataset.mbDot); return; }
    if (e.target.closest('#arlo-mb-atc')) addToCart();
  });

  // swipe between the preview and the photos
  (function swipe() {
    const media = el('arlo-mb-media');
    let x0 = null, y0 = null, locked = false;
    media.addEventListener('pointerdown', (e) => { x0 = e.clientX; y0 = e.clientY; locked = false; });
    media.addEventListener('pointermove', (e) => {
      if (x0 === null || locked) return;
      const dx = e.clientX - x0, dy = e.clientY - y0;
      if (Math.abs(dy) > Math.abs(dx)) { x0 = null; return; }   // let the page scroll
      if (Math.abs(dx) > 45) { stepSlide(dx < 0 ? 1 : -1); locked = true; x0 = null; }
    });
    media.addEventListener('pointerup', () => { x0 = null; });
    media.addEventListener('pointercancel', () => { x0 = null; });
    media.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') stepSlide(1);
      if (e.key === 'ArrowLeft') stepSlide(-1);
    });
  }());

  // ── cart ──────────────────────────────────────────────────────────────────
  function uploadToUploadcare(blob, filename) {
    const fd = new FormData();
    fd.append('UPLOADCARE_PUB_KEY', cfg.uploadcarePublicKey);
    fd.append('UPLOADCARE_STORE', '1');
    fd.append('file', blob, filename || 'magnet.png');
    return fetch('https://upload.uploadcare.com/base/', { method: 'POST', body: fd })
      .then((res) => res.json())
      .then((d) => {
        if (!d.file) throw new Error('Upload failed');
        return 'https://ucarecdn.com/' + d.file + '/' + (filename || 'magnet.png');
      });
  }

  // Small flat mockup of the exact arch colour + border design (the same tile
  // shown in the picker) for the cart thumbnail — no room scene, so it's tiny
  // and uploads near-instantly (a fraction of the old 900px room PNG).
  function cartThumbBlob() {
    return new Promise((resolve) => {
      const cv = document.createElement('canvas');
      cv.width = 500; cv.height = 500;
      drawBorderThumb(cv, currentBorder(), (currentColour() || {}).hex);
      cv.toBlob((b) => resolve(b), 'image/png');
    });
  }

  // Pre-warm the cart thumbnail in the background as the design settles, so the
  // Add-to-cart click is instant. Keyed by "colour|border"; a real mockup photo
  // (cfg.mockups) is preferred and needs no render.
  let _thumb = { sig: null, url: null, promise: null };
  let _thumbTimer = null;
  function warmThumb() {
    const key = mockupKey();
    if ((cfg.mockups || {})[key]) return;               // real photo exists — nothing to render
    if (_thumb.sig === key && (_thumb.url || _thumb.promise)) return;
    _thumb.sig = key; _thumb.url = null;
    _thumb.promise = cartThumbBlob()
      .then((blob) => uploadToUploadcare(blob, 'arch-magnet-' + data.handle + '.png'))
      .then((url) => { if (_thumb.sig === key) _thumb.url = url; return url; })
      .catch((e) => { if (_thumb.sig === key) _thumb.promise = null; throw e; });
  }
  function scheduleThumbWarm() {
    clearTimeout(_thumbTimer);
    _thumbTimer = setTimeout(() => { try { warmThumb(); } catch (e) {} }, 400);
  }
  async function getThumbUrl(setStatus) {
    const key = mockupKey();
    const mock = (cfg.mockups || {})[key];
    if (mock) return mock;                               // prefer a real per-combo photo
    if (_thumb.sig === key && _thumb.url) return _thumb.url;    // pre-warmed — instant
    if (_thumb.sig === key && _thumb.promise) {
      setStatus('Finishing preview…');
      try { return await _thumb.promise; } catch (e) { /* fall through to a fresh render */ }
    }
    setStatus('Preparing image…');
    const blob = await cartThumbBlob();
    return uploadToUploadcare(blob, 'arch-magnet-' + data.handle + '.png');
  }

  async function addToCart() {
    if (_atcBusy) return;
    const variant = colourVariant();
    if (!variant) return;
    _atcBusy = true;

    const overlay = el('arlo-mb-status');
    const setStatus = (m) => { el('arlo-mb-status-text').textContent = m; };
    overlay.style.display = 'flex';

    try {
      const colour = currentColour();
      const border = currentBorder();
      const size = (cfg.arch && cfg.arch.sizes || []).find((s) => s.enabled) || {};

      // Cart thumbnail = the small colour+border mockup (pre-warmed, so usually
      // instant), or a real per-combo photo if one is set in the admin.
      const previewUrl = await getThumbUrl(setStatus);
      setStatus('Adding to cart…');
      const props = {
        'Size': size.wMm ? size.wMm + ' × ' + size.hMm + ' mm' : '',
      };
      if (previewUrl) props['_image'] = previewUrl;
      // The colour is the variant, so it already shows on the line — repeating
      // it as a property would just duplicate it in the cart.
      // NOTE: no 'Border' property on the arch line — the border is a SEPARATE
      // cart line (its own "Border: …" + "For: … arch"), so listing it here too
      // made it look like the border came with the arch.

      // The arch itself: one line, stock tracked on this colour.
      const items = [{ id: variant.id, quantity: 1, properties: props }];

      // A border is its own product line at its own price. The saving comes
      // from a native Shopify automatic Buy X Get Y discount at checkout, so
      // nothing here has to fake a price.
      const addon = borderAddon();
      if (border && borderApplies()) {
        const borderVariantId = border.addonVariantId || (addon && addon.variantId);
        if (borderVariantId) {
          items.push({
            id: borderVariantId, quantity: 1,
            properties: Object.assign(
              previewUrl ? { '_image': previewUrl } : {},
              { 'Border': border.label, 'For': colour.name + ' arch' }
            ),
          });
        } else {
          console.warn('[ArloMagnets] no variant for border', border.label);
        }
      }

      // Extras such as crayons. The border is handled above with its
      // design-specific variant, so it's skipped here.
      for (const a of visibleAddons()) {
        if (a.kind === 'border') continue;
        if (!state.addons[a.id] || !a.variantId) continue;
        // No _image here: these are stock items, so the cart should show the
        // product's own photo rather than the customer's arch preview.
        items.push({
          id: a.variantId, quantity: 1,
          properties: { 'For': colour.name + ' arch' },
        });
      }

      setStatus('Adding to cart…');
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || 'Could not add to cart');
      }
      try { sessionStorage.setItem('arlo_open_cart', '1'); } catch (e) {}
      window.location.reload();
    } catch (e) {
      console.error('[ArloMagnets]', e);
      setStatus('⚠ ' + e.message);
      setTimeout(() => { overlay.style.display = 'none'; }, 3500);
      _atcBusy = false;
    }
  }

  // Live add-on prices (2026-10-11): read each add-on product's current price
  // and compare-at straight from Shopify on page load, so a price change (a
  // border design marked down, or the markdown removed) shows without
  // re-publishing the admin settings — those stored prices are only the
  // first-paint fallback.
  function loadLiveAddonPrices() {
    const handles = cfg.addonProductHandles || [];
    if (!handles.length || !window.fetch) return;
    Promise.all(handles.map((h) => fetch('/products/' + h + '.js', { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null)).catch(() => null)))
      .then((list) => {
        const byId = {};
        list.forEach((p) => ((p && p.variants) || []).forEach((v) => { byId[String(v.id)] = v; }));
        const apply = (vid, set) => {
          const v = byId[String(vid)];
          if (v) set(v.price / 100, v.compare_at_price > v.price ? v.compare_at_price / 100 : 0);
        };
        (cfg.borders || []).forEach((b) => apply(b.addonVariantId, (p, c) => { b.addonPrice = p; b.addonCompareAt = c; }));
        (cfg.addons || []).forEach((a) => {
          if (a.kind === 'border') return; // priced per design above
          apply(a.variantId, (p, c) => {
            a.fullPrice = p; a.compareAtPrice = c;
            if (a.noDiscount) a.discountedPrice = p;
            else if (a.discountAmount != null && a.discountAmount !== '') a.discountedPrice = Math.max(0, p - Number(a.discountAmount));
          });
        });
        renderAddons(); renderPrice();
      });
  }

  // ── go ────────────────────────────────────────────────────────────────────
  ready.then(() => {
    renderAll();
    loadLiveAddonPrices();
    // Show the Shopify lifestyle photos first; the live preview appears as soon
    // as the customer starts personalising (snapToPreview on any option pick).
    // Fall back to the preview if the product has no photos.
    const firstPhoto = slideKeys().find((k) => k !== 'preview');
    showSlide(firstPhoto || 'preview');
    scheduleThumbWarm(); // pre-build the default cart thumbnail in the background
  });
}());
